import { ErrorMessage } from '@hookform/error-message';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import viewOff from '../../assets/images/view-off-icon.svg';
import viewOn from '../../assets/images/view-on-icon.svg';
import CheckboxWithLabel from '../../components/layouts/CheckboxWithLabel';
import Button from '../../components/ui/Button';
import CustomInput from '../../components/ui/CustomInput';
import Skeleton from '../../components/ui/Skeleton';
import { ApiConstants } from '../../constants/ApiConstants';
import { OtpVerifyTypes, StorageConstants } from '../../constants/Constants';
import { Routes } from '../../constants/RouteConstants';
import useMakeRequest from '../../utils/ApiHelper';
import { getFromStorage } from '../../utils/Helper';
import SignUpValidation from '../../validations/auth/SignUpValidation';
import DoNotSellMyPersonalInfoModal from '../cms-public/DoNotSellMyPersonalInfoModal';

interface FormInputs {
  name: string;
  email: string;
  password: string;
  confirm_password: string;
  passcode: string;
  agree_tnc: boolean;
  fcm_token?: string | null;
};

const SignUp = () => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const signUp = useMakeRequest();
  const cmsPage = useMakeRequest();
  const [hasScrolledPrivacy, setHasScrolledPrivacy] = useState(false);
  const [hasScrolledTerms, setHasScrolledTerms] = useState(false);
  const [activeCmsPage, setActiveCmsPage] = useState<string | null>(null);
  const [showPwd, setShowPwd] = useState(false)
  const [showCnfPwd, setShowCnfPwd] = useState(false)
  const { register, watch, setValue, formState: { errors }, handleSubmit } = useForm<FormInputs>({
    mode: 'onChange',
    resolver: yupResolver(SignUpValidation),
    defaultValues: {
      agree_tnc: false
    }
  });

  const agreeTncValidation = {
    validate: () => {
      if (!hasScrolledPrivacy || !hasScrolledTerms) {
        return "Please read and accept the Privacy Policy & End User License Agreement.";
      }
      return true;
    }
  };


  const onSubmit = async (payload: FormInputs) => {
    const fcm_token = getFromStorage({ key: StorageConstants?.FCM_TOKEN })

    payload = {
      ...payload,
      fcm_token: fcm_token || ""
    };
    toast.promise(
      signUp.makeRequest({ payload, url: ApiConstants?.SignUp }),
      {
        loading: 'Registering...',
        success: (data: any) => data.message,
        error: (error: any) => error.message,
      }
    );
  }

  useEffect(() => {
    if (signUp?.status) {
      navigate(Routes?.Otp, { state: { email: watch('email'), verifyType: OtpVerifyTypes?.MAIL_VERIFY } })
    } else {
      navigate(Routes?.SignUp, { state: { email: null } })
    }
  }, [signUp?.status])

  useEffect(() => {
    const handleContentClick = (event: { target: any; preventDefault: () => void; }) => {
      const clickedElement = event.target;

      console.log("clickedElement: ", clickedElement);

      // Identify the link: "Do not share my personal info"
      // Still recommend using a more robust identifier (ID/class from CMS) if possible.
      if (clickedElement.textContent.includes('Do not share my personal info')) {
        event.preventDefault(); // Prevent default link behavior

        // Get a reference to the first modal element
        const firstModalElement = $('#exampleModal'); // jQuery selector

        // 1. Trigger the close button click on the first modal
        firstModalElement.find('.btn-close').click();

        // 2. Listen for the 'hidden.bs.modal' event on the first modal
        // This ensures the first modal is fully closed before opening the second.
        firstModalElement.one('hidden.bs.modal', function () {
          // 3. Show the second modal
          // @ts-ignore
          $('#personalLinkModal').modal('show');
        });
      }
    };

    const currentContentRef = contentRef.current;
    if (currentContentRef) {
      // Attach the event listener to the modal-body div
      currentContentRef.addEventListener('click', handleContentClick);
    }

    // Cleanup: remove the event listener when the component unmounts
    return () => {
      if (currentContentRef) {
        currentContentRef.removeEventListener('click', handleContentClick);
      }
    };
  }, [cmsPage]);

  const viewCmsPage = (slug: string) => {
    setActiveCmsPage(slug);
    if (slug === 'privacy-policy') setHasScrolledPrivacy(false);
    if (slug === 'terms-and-conditions') setHasScrolledTerms(false);
    cmsPage.makeRequest({ payload: { detail_id: slug }, url: ApiConstants?.CmsPagesDetail })
  }

  useEffect(() => {
    const isLogined = getFromStorage({ key: StorageConstants?.ACCESS_TOKEN })
    if (isLogined) {
      navigate(Routes?.Dashboard)
    }
  }, [])

  const handleScroll = (e: any, type: any) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 5) {
      if (type === 'privacy') setHasScrolledPrivacy(true);
      if (type === 'terms') setHasScrolledTerms(true);
    }
  };

  return (
    <main id="main-content" className="auth-page signup-page">
      <div className="auth-container">
        <h1 className='auth-title'>Sign Up</h1>

        <form onSubmit={handleSubmit(onSubmit)} className={`auth-form-wrap ${signUp?.loading ? 'disabled-ct' : ''}`}>
          <div className="auth-form">
            <div className="auth-form-fields">
              <CustomInput
                label="Name"
                type="text"
                placeholder="Enter name"
                required
                {...register('name')}
                error={errors.name?.message}
              />
              <CustomInput
                label="Email"
                type="email"
                placeholder="Enter email"
                required
                {...register('email')}
                error={errors.email?.message}
              />

              <CustomInput
                label="Password"
                type={showPwd ? 'text' : 'password'}
                placeholder="**********"
                required
                {...register('password')}
                error={errors.password?.message}
                icon={
                  showPwd ? <img className="calIcon" src={viewOn} alt="" /> : <img className="calIcon" src={viewOff} alt="" />
                }
                onIconClick={() => setShowPwd(!showPwd)}
              />

              <CustomInput
                label="Confirm Password"
                type={showCnfPwd ? 'text' : 'password'}
                placeholder="**********"
                required
                {...register('confirm_password')}
                error={errors.confirm_password?.message}
                icon={
                  showCnfPwd ? <img className="calIcon" src={viewOn} alt="" /> : <img className="calIcon" src={viewOff} alt="" />
                }
                onIconClick={() => setShowCnfPwd(!showCnfPwd)}
              />

              <CustomInput
                label="Passcode"
                type="text"
                placeholder="Enter passcode"
                required
                {...register('passcode')}
                error={errors.name?.message}
              />

              <CheckboxWithLabel
                register={() =>
                  register("agree_tnc", {
                    ...agreeTncValidation,
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      console.log("hasScrolledPrivacy: ", hasScrolledPrivacy)
                      console.log("hasScrolledTerms: ", hasScrolledTerms)
                      if (!hasScrolledPrivacy || !hasScrolledTerms) {
                        e.preventDefault();
                        setValue("agree_tnc", false, { shouldValidate: true });
                      } else {
                        setValue("agree_tnc", e.target.checked, { shouldValidate: true });
                      }
                    },
                  })
                }
                labelText="I accept the"
                links={[
                  {
                    text: 'Privacy Policy',
                    onClick: () => viewCmsPage('privacy-policy'),
                    isLink: true
                  },
                  {
                    text: '&',
                    isLink: false
                  },
                  {
                    text: 'End User License Agreement',
                    onClick: () => viewCmsPage('terms-and-conditions'),
                    isLink: true
                  },
                  {
                    text: '.',
                    isLink: false
                  },
                ]}
                errorMessage={
                  <ErrorMessage
                    errors={errors}
                    name="agree_tnc"
                    render={({ message }) => <p className="valid-error">{message}</p>}
                  />
                }
              />


            </div>

            <div className="auth-form-actions">
              <Button disabled={!hasScrolledPrivacy || !hasScrolledTerms} className='com-btn mt-5' type='submit' loading={signUp?.loading}>Sign Up</Button>
              <div className='auth-footer-text'>Have an account? <Link to="/login" className='auth-link'>Sign In</Link></div>
            </div>
          </div>
        </form>

        <div ref={contentRef} className="modal fade inner-modal c" id="exampleModal" tabIndex={-1} aria-labelledby="exampleModalLabel" aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h1 className="modal-title fs-5" id="exampleModalLabel">{cmsPage?.data?.data?.title}</h1>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div className="modal-body"
                style={{ maxHeight: '400px', overflowY: 'auto' }}
                onScroll={(e) => handleScroll(e, activeCmsPage === 'privacy-policy' ? 'privacy' : 'terms')}
              >
                {
                  cmsPage?.loading ?
                    <>
                      <Skeleton className='skeleton-loader mt-5' style={{ width: '50%', height: '20px' }} />
                      <Skeleton className='skeleton-loader my-2' style={{ width: '60%', height: '20px' }} />
                      <Skeleton className='skeleton-loader my-2' style={{ width: '100%', height: '20px' }} />
                      <Skeleton className='skeleton-loader my-2' style={{ width: '80%', height: '20px' }} />
                      <Skeleton className='skeleton-loader my-2' style={{ width: '90%', height: '20px' }} />

                      <Skeleton className='skeleton-loader mt-5' style={{ width: '50%', height: '20px' }} />
                      <Skeleton className='skeleton-loader my-2' style={{ width: '60%', height: '20px' }} />
                      <Skeleton className='skeleton-loader my-2' style={{ width: '100%', height: '20px' }} />
                      <Skeleton className='skeleton-loader my-2' style={{ width: '80%', height: '20px' }} />
                      <Skeleton className='skeleton-loader my-2' style={{ width: '90%', height: '20px' }} />
                    </>
                    :
                    <p dangerouslySetInnerHTML={{ __html: cmsPage?.data?.data?.content }}></p>
                }
              </div>
            </div>
          </div>
        </div>
        <DoNotSellMyPersonalInfoModal cmsPage={cmsPage} />
      </div>
    </main>
  )
}

export default SignUp